import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { BASE_URL } from '../api/api';


// SVG Icons (keep your existing icons)
const UserIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const EmailIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
);

const PhoneIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);

const LicenseIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const AddressIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const CalendarIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const UploadIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
);

const CloseIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const AddClient = ({ onClientAdded, onClientUpdated, editingClient, onCancel }) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';

    const [formData, setFormData] = useState({
        // Main client personal info
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        date_naissance: '',
        lieu_naissance: '',

        // Main client license info
        numero_permis: '',
        date_delivrance_permis: '',
        permis_delivre_le: '',
        lieu_permet: '',
        permis_validite: '',

        // Main client passport info
        numero_passport: '',
        passport_delivre_le: '',
        lieu_passport: '',

        // Second driver personal info
        conducteur2_nom: '',
        conducteur2_prenom: '',
        conducteur2_date_naissance: '',
        conducteur2_lieu_naissance: '',
        conducteur2_adresse: '',
        conducteur2_telephone: '',

        // Second driver license info
        conducteur2_numero_permis: '',
        conducteur2_date_delivrance_permis: '',
        conducteur2_permis_delivre_le: '',
        conducteur2_permis_validite: '',

        // Second driver passport info
        conducteur2_numero_passport: '',
        conducteur2_passport_delivre_le: '',
        conducteur2_lieu_permet: '',
        conducteur2_lieu_passport: '',

        // Additional fields
        client_type: 'particulier',
        photo_permis: null
    });

    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('main'); // 'main' or 'second'

    // Format date for input field (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            // If it's already in YYYY-MM-DD format, return as is
            if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                return dateString;
            }

            // If it's a Date object or other format, convert to YYYY-MM-DD
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Initialize form with editingClient data if provided
    React.useEffect(() => {
        if (editingClient) {
            console.log('Editing client data:', editingClient); // Debug log

            setFormData(prev => ({
                ...prev,
                // Main client
                nom: editingClient.nom || '',
                prenom: editingClient.prenom || '',
                email: editingClient.email || '',
                telephone: editingClient.telephone || '',
                adresse: editingClient.adresse || '',
                date_naissance: formatDateForInput(editingClient.date_naissance) || '',
                lieu_naissance: editingClient.lieu_naissance || '',
                numero_permis: editingClient.numero_permis || '',
                date_delivrance_permis: formatDateForInput(editingClient.date_delivrance_permis) || '',
                permis_delivre_le: editingClient.permis_delivre_le || '',
                lieu_permet: editingClient.lieu_permet || '',
                permis_validite: formatDateForInput(editingClient.permis_validite) || '',
                numero_passport: editingClient.numero_passport || '',
                passport_delivre_le: formatDateForInput(editingClient.passport_delivre_le) || '',
                lieu_passport: editingClient.lieu_passport || '',

                // Second driver
                conducteur2_nom: editingClient.conducteur2_nom || '',
                conducteur2_prenom: editingClient.conducteur2_prenom || '',
                conducteur2_date_naissance: formatDateForInput(editingClient.conducteur2_date_naissance) || '',
                conducteur2_lieu_naissance: editingClient.conducteur2_lieu_naissance || '',
                conducteur2_adresse: editingClient.conducteur2_adresse || '',
                conducteur2_telephone: editingClient.conducteur2_telephone || '',
                conducteur2_numero_permis: editingClient.conducteur2_numero_permis || '',
                conducteur2_date_delivrance_permis: formatDateForInput(editingClient.conducteur2_date_delivrance_permis) || '',
                conducteur2_permis_delivre_le: editingClient.conducteur2_permis_delivre_le || '',
                conducteur2_permis_validite: formatDateForInput(editingClient.conducteur2_permis_validite) || '',
                conducteur2_numero_passport: editingClient.conducteur2_numero_passport || '',
                conducteur2_passport_delivre_le: formatDateForInput(editingClient.conducteur2_passport_delivre_le) || '',
                conducteur2_lieu_permet: editingClient.conducteur2_lieu_permet || '',
                conducteur2_lieu_passport: editingClient.conducteur2_lieu_passport || '',

                client_type: editingClient.client_type || 'particulier',
                photo_permis: null
            }));
        }
    }, [editingClient]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'photo_permis') {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('');

        try {
            const formDataToSend = new FormData();

            // Append ALL fields to FormData, including empty ones
            Object.entries(formData).forEach(([key, value]) => {
                // For file inputs, only append if there's a file
                if (key === 'photo_permis') {
                    if (value && value instanceof File) {
                        formDataToSend.append(key, value);
                    }
                } else {
                    // For all other fields, append even if empty
                    // Convert null/undefined to empty string
                    const fieldValue = value === null || value === undefined ? '' : value;
                    formDataToSend.append(key, fieldValue);
                }
            });

            const method = editingClient ? 'put' : 'post';
            const endpoint = editingClient ? `/clients/${editingClient.id}` : '/clients';

            console.log('Submitting data:', Object.fromEntries(formDataToSend)); // Debug log

            const response = await api[method](endpoint, formDataToSend, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const result = response.data;

            setMessage(editingClient ? t('success_update_client') : t('success_add_client'));

            if (editingClient) {
                onClientUpdated(result);
            } else {
                onClientAdded(result);
                // Reset form only for new clients
                setFormData({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    adresse: '',
                    date_naissance: '',
                    lieu_naissance: '',
                    numero_permis: '',
                    date_delivrance_permis: '',
                    permis_delivre_le: '',
                    lieu_permet: '',
                    permis_validite: '',
                    numero_passport: '',
                    passport_delivre_le: '',
                    lieu_passport: '',
                    conducteur2_nom: '',
                    conducteur2_prenom: '',
                    conducteur2_date_naissance: '',
                    conducteur2_lieu_naissance: '',
                    conducteur2_adresse: '',
                    conducteur2_telephone: '',
                    conducteur2_numero_permis: '',
                    conducteur2_date_delivrance_permis: '',
                    conducteur2_permis_delivre_le: '',
                    conducteur2_permis_validite: '',
                    conducteur2_numero_passport: '',
                    conducteur2_passport_delivre_le: '',
                    conducteur2_lieu_permet: '',
                    conducteur2_lieu_passport: '',
                    client_type: 'particulier',
                    photo_permis: null
                });
            }
        } catch (error) {
            console.error("Erreur:", error);
            setMessage(error.message || "Une erreur s'est produite");
        } finally {
            setIsSubmitting(false);
        }
    };
    const renderMainDriverFields = () => (
        <div className="space-y-5">
            <h3 className={`text-lg font-semibold text-gray-800 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                <UserIcon className={isRtl ? 'ml-2' : 'mr-2'} />
                {t('personal_info')} ({t('main_driver')})
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('last_name')}*</label>
                    <div className="relative">
                        <input
                            name="nom"
                            type="text"
                            value={formData.nom}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                            required
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <UserIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('first_name')}*</label>
                    <div className="relative">
                        <input
                            name="prenom"
                            type="text"
                            value={formData.prenom}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                            required
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <UserIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('email')}</label>
                    <div className="relative">
                        <input
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <EmailIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')}*</label>
                    <div className="relative">
                        <input
                            name="telephone"
                            type="text"
                            value={formData.telephone}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                            required
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <PhoneIcon />
                        </div>
                    </div>
                </div>

                <div className="relative md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('address')}</label>
                    <div className="relative">
                        <input
                            name="adresse"
                            type="text"
                            value={formData.adresse}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <AddressIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('dob')}</label>
                    <div className="relative">
                        <input
                            name="date_naissance"
                            type="date"
                            value={formData.date_naissance}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <CalendarIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('pob')}</label>
                    <div className="relative">
                        <input
                            name="lieu_naissance"
                            type="text"
                            value={formData.lieu_naissance}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <UserIcon />
                        </div>
                    </div>
                </div>
            </div>

            <h3 className={`text-lg font-semibold text-gray-800 flex items-center mt-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <LicenseIcon className={isRtl ? 'ml-2' : 'mr-2'} />
                {t('driving_license')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('license_number')}*</label>
                    <div className="relative">
                        <input
                            name="numero_permis"
                            type="text"
                            value={formData.numero_permis}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                            required
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <LicenseIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_date')}</label>
                    <div className="relative">
                        <input
                            name="date_delivrance_permis"
                            type="date"
                            value={formData.date_delivrance_permis}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <CalendarIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_place')}</label>
                    <div className="relative">
                        <input
                            name="permis_delivre_le"
                            type="text"
                            value={formData.permis_delivre_le}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <LicenseIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('validity')}</label>
                    <div className="relative">
                        <input
                            name="permis_validite"
                            type="date"
                            value={formData.permis_validite}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <CalendarIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('issued_at')}</label>
                    <div className="relative">
                        <input
                            name="lieu_permet"
                            type="text"
                            value={formData.lieu_permet}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <LicenseIcon />
                        </div>
                    </div>
                </div>
            </div>

            <h3 className={`text-lg font-semibold text-gray-800 flex items-center mt-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <LicenseIcon className={isRtl ? 'ml-2' : 'mr-2'} />
                {t('passport')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('passport_number')}</label>
                    <div className="relative">
                        <input
                            name="numero_passport"
                            type="text"
                            value={formData.numero_passport}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <LicenseIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('passport_issue_date')}</label>
                    <div className="relative">
                        <input
                            name="passport_delivre_le"
                            type="date"
                            value={formData.passport_delivre_le}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <CalendarIcon />
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('passport_issue_place')}</label>
                    <div className="relative">
                        <input
                            name="lieu_passport"
                            type="text"
                            value={formData.lieu_passport}
                            onChange={handleChange}
                            className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50`}
                        />
                        <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <LicenseIcon />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSecondDriverFields = () => (
        <div className="space-y-5">
            <h3 className={`text-lg font-semibold text-gray-800 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                <UserIcon className={isRtl ? 'ml-2' : 'mr-2'} />
                {t('secondary_driver_info')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('last_name')}</label>
                    <input
                        name="conducteur2_nom"
                        type="text"
                        value={formData.conducteur2_nom}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('first_name')}</label>
                    <input
                        name="conducteur2_prenom"
                        type="text"
                        value={formData.conducteur2_prenom}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('dob')}</label>
                    <input
                        name="conducteur2_date_naissance"
                        type="date"
                        value={formData.conducteur2_date_naissance}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('pob')}</label>
                    <input
                        name="conducteur2_lieu_naissance"
                        type="text"
                        value={formData.conducteur2_lieu_naissance}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('address')}</label>
                    <input
                        name="conducteur2_adresse"
                        type="text"
                        value={formData.conducteur2_adresse}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')}</label>
                    <input
                        name="conducteur2_telephone"
                        type="text"
                        value={formData.conducteur2_telephone}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('license_number')}</label>
                    <input
                        name="conducteur2_numero_permis"
                        type="text"
                        value={formData.conducteur2_numero_permis}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_date')}</label>
                    <input
                        name="conducteur2_date_delivrance_permis"
                        type="date"
                        value={formData.conducteur2_date_delivrance_permis}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_place')}</label>
                    <input
                        name="conducteur2_permis_delivre_le"
                        type="text"
                        value={formData.conducteur2_permis_delivre_le}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('validity')}</label>
                    <input
                        name="conducteur2_permis_validite"
                        type="date"
                        value={formData.conducteur2_permis_validite}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('passport_number')}</label>
                    <input
                        name="conducteur2_numero_passport"
                        type="text"
                        value={formData.conducteur2_numero_passport}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('passport_issue_date')}</label>
                    <input
                        name="conducteur2_passport_delivre_le"
                        type="date"
                        value={formData.conducteur2_passport_delivre_le}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('issue_place')} 2</label>
                    <input
                        name="conducteur2_lieu_permet"
                        type="text"
                        value={formData.conducteur2_lieu_permet}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('passport_issue_place')} 2</label>
                    <input
                        name="conducteur2_lieu_passport"
                        type="text"
                        value={formData.conducteur2_lieu_passport}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 ${isRtl ? 'text-right' : 'text-left'}`}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className={`border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 bg-white z-10 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <h2 className={`text-xl font-bold text-gray-800 ${isRtl ? 'text-right' : 'text-left'}`}>
                    {editingClient ? t('edit_client') : t('new_client')}
                </h2>
                <button
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-500 p-1 rounded-full hover:bg-gray-100"
                >
                    <CloseIcon />
                </button>
            </div>

            {/* Form Content */}
            <div className="p-6">
                {/* Tab Navigation */}
                <div className={`flex border-b border-gray-200 mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('main')}
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'main'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {t('main_driver')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('second')}
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'second'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {t('secondary_driver')}
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {activeTab === 'main' ? renderMainDriverFields() : renderSecondDriverFields()}

                    {/* Common Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                        <div className="relative">
                            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRtl ? 'text-right' : 'text-left'}`}>{t('client_type_label')}</label>
                            <div className="relative">
                                <select
                                    name="client_type"
                                    value={formData.client_type}
                                    onChange={handleChange}
                                    className={`w-full ${isRtl ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 appearance-none`}
                                >
                                    <option value="particulier">{t('particulier')}</option>
                                    <option value="entreprise">{t('entreprise')}</option>
                                </select>
                                <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRtl ? 'text-right' : 'text-left'}`}>{t('license_photo')}</label>
                            <label className={`flex items-center ${isRtl ? 'space-x-reverse space-x-4' : 'space-x-4'} cursor-pointer`}>
                                <div className="flex-1">
                                    <div className="px-6 py-3 border-2 border-dashed border-gray-200 rounded-lg hover:border-blue-400 transition-colors group">
                                        <div className="flex flex-col items-center justify-center text-center">
                                            <UploadIcon className="w-6 h-6 text-blue-500 mb-1 group-hover:text-blue-600 transition-colors" />
                                            <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                                                {formData.photo_permis ? formData.photo_permis.name : t('click_to_upload')}
                                            </span>
                                        </div>
                                        <input
                                            name="photo_permis"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleChange}
                                            className="hidden"
                                        />
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Message and Actions */}
                    <div className="pt-4">
                        {message && (
                            <div className={`mb-4 p-3 rounded-lg ${isRtl ? 'text-right' : 'text-left'} ${message.includes('succès') || message.includes('success') || message.includes('بنجاح') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <svg className={`w-5 h-5 ${isRtl ? 'ml-2' : 'mr-2'} ${message.includes('succès') || message.includes('success') || message.includes('بنجاح') ? 'text-green-500' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {message.includes('succès') || message.includes('success') || message.includes('بنجاح') ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        )}
                                    </svg>
                                    {message}
                                </div>
                            </div>
                        )}

                        <div className={`flex justify-end ${isRtl ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                            <button
                                type="button"
                                onClick={onCancel}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                disabled={isSubmitting}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className={`animate-spin h-4 w-4 text-white ${isRtl ? 'ml-2' : 'mr-2'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('submitting')}
                                    </>
                                ) : (
                                    <>
                                        <svg className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        {editingClient ? t('update') : t('save')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddClient;